import { useQuery } from 'react-query';

import { Button, Spinner } from 'modules/v2/common/AtomicDesign/atoms';
import { getModAssets } from 'modules/api/digitalServices/mailOnDemand';
import { cn } from 'modules/v2/common/utils/cn';

import strings, { formatMessage } from '../../strings';
import { formatDate } from '../../utils';

const assetKey = (asset) => `${asset.kind}:${asset.sourceId}`;

// Raw machine export filename: export-<id>-<ms epoch>.pdf
const RAW_EXPORT = /^export-[a-z0-9]+-(\d{12,13})\.pdf$/i;

const prettyAssetTitle = (asset) => {
  if (asset.kind !== 'tb_export') return asset.title;
  const m = (asset.title || '').match(RAW_EXPORT);
  if (m) {
    const ts = Number(m[1]);
    return ts > 1e12 && ts < 2e12
      ? formatMessage(strings.assets.templateExportOn, { date: formatDate(new Date(ts)) })
      : strings.assets.templateExportName;
  }
  return (asset.title || '').replace(/\.pdf$/i, '') || strings.assets.templateExportName;
};

const AssetStep = ({
  selectedAsset,
  onSelectAsset,
  onNext,
  coverLetter = null,
  onAddCoverLetter,
  onEditCoverLetter,
  onRemoveCoverLetter,
}) => {
  const { data: assets, isLoading, isError, refetch } = useQuery('modAssets', getModAssets);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <Spinner />
        <span className="text-neutral-500">{strings.assets.loading}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-start gap-3 py-6">
        <p className="text-neutral-800">{strings.assets.loadError}</p>
        <Button color="outlined" onClick={() => refetch()}>
          {strings.common.retry}
        </Button>
      </div>
    );
  }

  if (!assets || assets.length === 0) {
    return (
      <div className="py-12 text-center" data-testid="mod-assets-empty">
        <h3 className="mb-2 text-lg font-semibold">{strings.assets.emptyTitle}</h3>
        <p className="mx-auto max-w-lg text-neutral-500">{strings.assets.emptyBody}</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-1 text-lg font-semibold">{strings.assets.title}</h2>
      <p className="mb-6 text-neutral-500">{strings.assets.subtitle}</p>

      {/* Masonry (CSS columns): covers show in full at their natural ratio
          instead of being cropped to a fixed height. Each card avoids
          breaking across columns; media is letterboxed within a bound so a
          tall/large asset can't dominate the grid. */}
      <div className="columns-1 gap-4 sm:columns-2 lg:columns-3">
        {assets.map((asset) => {
          const isSelected = selectedAsset && assetKey(selectedAsset) === assetKey(asset);
          // Member-facing preview: the digital-book read page (task 35) when
          // present, else the generated preview/PDF.
          const previewHref =
            asset.previewPageUrl || asset.previewUrl || asset.pdfUrl;

          return (
            <button
              type="button"
              key={assetKey(asset)}
              data-testid={`mod-asset-${asset.sourceId}`}
              onClick={() => onSelectAsset(asset)}
              className={cn(
                'mb-4 block w-full break-inside-avoid overflow-hidden rounded-lg border bg-white text-left transition-colors',
                isSelected
                  ? 'border-neutral-800 ring-2 ring-neutral-800'
                  : 'border-neutral-200 hover:border-neutral-400',
              )}
            >
              {asset.thumbnailUrl ? (
                <div className="flex items-center justify-center bg-neutral-100">
                  <img
                    src={asset.thumbnailUrl}
                    alt={asset.title}
                    loading="lazy"
                    className="max-h-[420px] w-full object-contain"
                  />
                </div>
              ) : (
                <div
                  className="flex h-32 w-full flex-col items-center justify-center gap-2 bg-neutral-50 text-neutral-400"
                  data-testid={`mod-asset-doc-placeholder-${asset.sourceId}`}
                >
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                    <path d="M14 3v5h5" />
                  </svg>
                  <span className="text-xs font-medium text-neutral-400">
                    {strings.assets.kindTbExport}
                  </span>
                </div>
              )}
              <div className="p-3">
                <div className="font-medium text-neutral-800" title={asset.title}>
                  {prettyAssetTitle(asset)}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="rounded bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-600">
                    {asset.kind === 'book'
                      ? strings.assets.kindBook
                      : strings.assets.kindTbExport}
                  </span>
                  {asset.printReady === false && (
                    <span className="text-[11px] text-amber-700">
                      {strings.assets.beingPrepared}
                    </span>
                  )}
                </div>
                {previewHref && (
                  <a
                    href={previewHref}
                    target="_blank"
                    rel="noreferrer noopener"
                    onClick={(event) => event.stopPropagation()}
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary-700"
                    data-testid={`mod-asset-preview-${asset.sourceId}`}
                  >
                    {asset.kind === 'book'
                      ? strings.assets.previewBook
                      : strings.assets.previewPdf}
                  </a>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Cover-letter add-on (V4): only offered once a book is chosen. */}
      {selectedAsset && onAddCoverLetter && (
        <div className="mt-6" data-testid="mod-cover-letter-addon">
          {coverLetter ? (
            <div className="flex items-center gap-4 rounded-2xl border border-primary-300 bg-primary-50 p-4">
              <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-primary-500 text-white">✉</span>
              <div className="flex-1">
                <div className="font-semibold text-neutral-800">
                  {strings.coverLetter.summaryLetter}
                  <span className="ml-2 rounded bg-success-100 px-2 py-0.5 text-[11px] font-semibold text-success-700">
                    {strings.coverLetter.addedChip}
                  </span>
                </div>
                <div className="text-sm text-neutral-500">
                  {formatMessage(strings.coverLetter.summaryLetterMeta, {
                    template:
                      coverLetter.templateKey === 'modern'
                        ? strings.coverLetter.templateModern
                        : strings.coverLetter.templateClassic,
                  })}
                </div>
              </div>
              <Button color="outlined" onClick={onEditCoverLetter} data-testid="mod-cover-letter-edit">
                {strings.review.edit}
              </Button>
              <Button color="dangerOutlined" onClick={onRemoveCoverLetter} data-testid="mod-cover-letter-remove">
                {strings.coverLetter.summaryRemove}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-4 rounded-2xl border border-primary-300 bg-gradient-to-b from-white to-primary-50 p-4">
              <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-primary-100 text-primary-700">✉</span>
              <div className="flex-1">
                <div className="font-semibold text-neutral-800">
                  {strings.coverLetter.addonTitle}
                  <span className="ml-2 rounded bg-success-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                    {strings.coverLetter.addonNew}
                  </span>
                </div>
                <div className="text-sm text-neutral-500">{strings.coverLetter.addonBody}</div>
              </div>
              <Button onClick={onAddCoverLetter} data-testid="mod-cover-letter-add">
                {strings.coverLetter.addonCta} →
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="mt-8 flex items-center justify-between">
        <div className="text-sm text-neutral-500">
          {selectedAsset && (
            <span data-testid="mod-mailing-summary">
              {strings.coverLetter.summaryBook}: <b className="text-neutral-800">{selectedAsset.title}</b>
              {coverLetter && <> &nbsp;+&nbsp; {strings.coverLetter.summaryLetter}</>}
            </span>
          )}
        </div>
        <Button disabled={!selectedAsset} onClick={onNext} data-testid="mod-asset-next">
          {strings.common.next}
        </Button>
      </div>
    </div>
  );
};

export default AssetStep;
